import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plane, Plus, Edit2, Trash2, Search, User, Wrench,
  MapPin, Loader, CheckCircle, AlertCircle, X, UserPlus
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertCircle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertCircle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Helper pour formater les erreurs ─────────────────────────────────────────
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  console.error('Full error:', error);
  
  if (error.response?.data) {
    const data = error.response.data;
    
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join('\n• ');
    }
    
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`• ${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`• ${key}: ${value}`);
        } else if (value?.msg) {
          errors.push(`• ${key}: ${value.msg}`);
        }
      });
      if (errors.length > 0) return errors.join('\n');
    }
  }
  
  if (error.message) return error.message;
  return defaultMessage;
};

// Conversions USA <-> métrique
const lbsToKg = lbs => lbs ? Math.round(lbs * 0.453592) : '';
const kgToLbs = kg  => kg  ? Math.round(kg  / 0.453592) : '';
const galToL  = gal => gal ? Math.round(gal * 3.78541)  : '';
const lToGal  = l   => l   ? +(l / 3.78541).toFixed(1)  : '';

// Convertit MM/DD/YYYY (FAA) → YYYY-MM-DD (HTML date input)
const faaDateToISO = (d) => {
  if (!d) return '';
  const m = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : d;
};

const EMPTY_FORM = {
  owner_id:               '',
  tail_number:            '',
  aircraft_type:          '',
  manufacturer:           '',
  model:                  '',
  year:                   '',
  year_manufactured:      '',
  serial_number:          '',
  callsign:               '',
  icao_code:              '',
  engine_type:            '',
  engine_manufacturer:    '',
  engine_model:           '',
  engine_count:           '',
  seats:                  '',
  fuel_type:              'JetA',
  fuel_capacity_gallons:  '',
  max_fuel_gallons:       '',
  max_takeoff_weight_lbs: '',
  max_landing_weight_lbs: '',
  home_base:              '',
  parking_spot:           '',
  owner_name:             '',
  owner_address:          '',
  owner_city:             '',
  owner_state:            '',
  owner_zip:              '',
  owner_country:          'USA',
  owner_phone:            '',
  owner_email:            '',
  airworthiness_date:     '',
  certificate_issue_date: '',
};

const EMPTY_NEW_OWNER = {
  email:      '',
  first_name: '',
  last_name:  '',
  phone:      '',
};

const AircraftView = () => {
  const [aircraft, setAircraft]               = useState([]);
  const [clients, setClients]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showModal, setShowModal]             = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [searchTerm, setSearchTerm]           = useState('');
  const [formData, setFormData]               = useState(EMPTY_FORM);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState(null);
  const [lookupStatus, setLookupStatus]       = useState(null);
  const [toast, setToast]                     = useState(null);
  const lookupTimer                           = useRef(null);

  // ── New Owner sub-modal ────────────────────────────────────
  const [showNewOwnerModal, setShowNewOwnerModal] = useState(false);
  const [newOwnerForm, setNewOwnerForm]           = useState(EMPTY_NEW_OWNER);
  const [savingOwner, setSavingOwner]             = useState(false);
  const [ownerError, setOwnerError]               = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  /* ── Fetch ──────────────────────────────────────────────── */
  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [acRes, clRes] = await Promise.all([
        axios.get(`${API_BASE}/aircraft/`, { withCredentials: true }),  
        axios.get(`${API_BASE}/owners/`, { withCredentials: true }),    
      ]);
      setAircraft(Array.isArray(acRes.data) ? acRes.data : []);
      setClients(Array.isArray(clRes.data)  ? clRes.data  : []);
    } catch (err) {
      setError('Unable to load data. Please check that the server is running.');
      showToast(formatErrorMessage(err, 'Failed to load aircraft data'), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  /* ── Auto-lookup ────────────────────────────────────────── */
  const handleTailNumberChange = (value) => {
    const tail = value.toUpperCase();
    field('tail_number', tail);
    setLookupStatus(null);
    if (tail.length < 4) return;
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => triggerLookup(tail), 800);
  };

  const triggerLookup = async (tail) => {
    setLookupStatus('loading');
    try {
      const { data } = await axios.get(`${API_BASE}/lookup/${tail}`, { withCredentials: true });
      console.log('[Lookup]', data);
      setFormData(prev => ({
        ...prev,
        manufacturer:           data.manufacturer           || prev.manufacturer,
        model:                  data.model                  || prev.model,
        aircraft_type:          data.aircraft_type          || prev.aircraft_type,
        year_manufactured:      data.year_manufactured      || prev.year_manufactured,
        serial_number:          data.serial_number          || prev.serial_number,
        icao_code:              data.icao_code              || prev.icao_code,
        engine_type:            data.engine_type            || prev.engine_type,
        engine_manufacturer:    data.engine_manufacturer    || prev.engine_manufacturer,
        engine_model:           data.engine_model           || prev.engine_model,
        engine_count:           data.engine_count           || prev.engine_count,
        airworthiness_date:     faaDateToISO(data.airworthiness_date)     || prev.airworthiness_date,
        certificate_issue_date: faaDateToISO(data.certificate_issue_date) || prev.certificate_issue_date,
        max_takeoff_weight_lbs: data.max_takeoff_weight_lbs || prev.max_takeoff_weight_lbs,
        max_landing_weight_lbs: data.max_landing_weight_lbs || prev.max_landing_weight_lbs,
        owner_name:    data.owner_name    || prev.owner_name,
        owner_address: data.owner_address || prev.owner_address,
        owner_city:    data.owner_city    || prev.owner_city,
        owner_state:   data.owner_state   || prev.owner_state,
        owner_zip:     data.owner_zip     || prev.owner_zip,
        owner_country: data.owner_country || prev.owner_country,
        owner_phone:   data.owner_phone   || prev.owner_phone,
      }));
      setLookupStatus('found');
      showToast('FAA data imported successfully', 'success');
    } catch (err) {
      setLookupStatus(err.response?.status === 404 ? 'notfound' : 'error');
      if (err.response?.status !== 404) {
        showToast(formatErrorMessage(err, 'FAA lookup failed'), 'error');
      }
    }
  };

  /* ── Submit aircraft ────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!formData.tail_number) {
      showToast('Tail number is required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const lbs_mtow = formData.max_takeoff_weight_lbs ? parseInt(formData.max_takeoff_weight_lbs) : undefined;
      const lbs_mlw  = formData.max_landing_weight_lbs  ? parseInt(formData.max_landing_weight_lbs)  : undefined;
      const gal_cap  = formData.fuel_capacity_gallons  ? parseFloat(formData.fuel_capacity_gallons)  : undefined;
      const gal_max  = formData.max_fuel_gallons        ? parseFloat(formData.max_fuel_gallons)        : undefined;

      const payload = {
        ...formData,
        owner_id:               formData.owner_id || undefined,
        year:                   formData.year              ? parseInt(formData.year)              : undefined,
        year_manufactured:      formData.year_manufactured  ? parseInt(formData.year_manufactured)  : undefined,
        seats:                  formData.seats              ? parseInt(formData.seats)              : undefined,
        engine_count:           formData.engine_count       ? parseInt(formData.engine_count)       : undefined,
        airworthiness_date:     formData.airworthiness_date     || undefined,
        certificate_issue_date: formData.certificate_issue_date || undefined,
        max_takeoff_weight_lbs: lbs_mtow,
        max_takeoff_weight_kg:  lbs_mtow ? lbsToKg(lbs_mtow) : undefined,
        max_landing_weight_lbs: lbs_mlw,
        max_landing_weight_kg:  lbs_mlw  ? lbsToKg(lbs_mlw)  : undefined,
        mtow_kg:                lbs_mtow ? lbsToKg(lbs_mtow) : undefined,
        fuel_capacity_gallons:  gal_cap,
        fuel_capacity_liters:   gal_cap ? galToL(gal_cap) : undefined,
        max_fuel_gallons:       gal_max,
        max_fuel_liters:        gal_max  ? galToL(gal_max)  : undefined,
      };

      if (editingAircraft) {
        await axios.put(`${API_BASE}/aircraft/${editingAircraft.id}`, payload, { withCredentials: true });
        showToast(`Aircraft ${formData.tail_number} updated successfully`, 'success');
      } else {
        await axios.post(`${API_BASE}/aircraft`, payload, { withCredentials: true });
        showToast(`Aircraft ${formData.tail_number} created successfully`, 'success');
      }
      await fetchData();
      closeModal();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error saving aircraft'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ─────────────────────────────────────────────── */
  const handleDelete = async (ac) => {
    if (!window.confirm(`Delete aircraft ${ac.tail_number}?`)) return;
    try {
      await axios.delete(`${API_BASE}/aircraft/${ac.id}`, { withCredentials: true });
      showToast(`Aircraft ${ac.tail_number} deleted successfully`, 'success');
      await fetchData();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error deleting aircraft'), 'error');
    }
  };

  /* ── Modal helpers ──────────────────────────────────────── */
  const openCreateModal = () => {
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    setShowModal(true);
  };

  const openEditModal = (ac) => {
    setEditingAircraft(ac);
    setFormData({
      owner_id:               ac.owner_id               ?? '',
      tail_number:            ac.tail_number             ?? '',
      aircraft_type:          ac.aircraft_type           ?? '',
      manufacturer:           ac.manufacturer            ?? '',
      model:                  ac.model                   ?? '',
      year:                   ac.year                    ?? '',
      year_manufactured:      ac.year_manufactured       ?? '',
      serial_number:          ac.serial_number           ?? '',
      callsign:               ac.callsign                ?? '',
      icao_code:              ac.icao_code               ?? '',
      engine_type:            ac.engine_type             ?? '',
      engine_manufacturer:    ac.engine_manufacturer     ?? '',
      engine_model:           ac.engine_model            ?? '',
      engine_count:           ac.engine_count            ?? '',
      seats:                  ac.seats                   ?? '',
      fuel_type:              ac.fuel_type               ?? 'JetA',
      fuel_capacity_gallons:  ac.fuel_capacity_gallons ?? (ac.fuel_capacity_liters ? lToGal(ac.fuel_capacity_liters) : ''),
      max_fuel_gallons:       ac.max_fuel_gallons      ?? (ac.max_fuel_liters      ? lToGal(ac.max_fuel_liters)      : ''),
      max_takeoff_weight_lbs: ac.max_takeoff_weight_lbs ?? (ac.max_takeoff_weight_kg ? kgToLbs(ac.max_takeoff_weight_kg) : '') ?? (ac.mtow_kg ? kgToLbs(ac.mtow_kg) : ''),
      max_landing_weight_lbs: ac.max_landing_weight_lbs ?? (ac.max_landing_weight_kg ? kgToLbs(ac.max_landing_weight_kg) : ''),
      home_base:              ac.home_base              ?? '',
      parking_spot:           ac.parking_spot           ?? '',
      owner_name:             ac.owner_name             ?? '',
      owner_address:          ac.owner_address          ?? '',
      owner_city:             ac.owner_city             ?? '',
      owner_state:            ac.owner_state            ?? '',
      owner_zip:              ac.owner_zip              ?? '',
      owner_country:          ac.owner_country          ?? 'USA',
      owner_phone:            ac.owner_phone            ?? '',
      owner_email:            ac.owner_email            ?? '',
      airworthiness_date:     ac.airworthiness_date     ?? '',
      certificate_issue_date: ac.certificate_issue_date ?? '',
    });
    setLookupStatus(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    clearTimeout(lookupTimer.current);
  };

  const field = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  /* ── New Owner sub-modal ────────────────────────────────── */
  const openNewOwnerModal = () => {
    setNewOwnerForm({
      email:      formData.owner_email || '',
      first_name: '',
      last_name:  formData.owner_name  || '',
      phone:      formData.owner_phone || '',
    });
    setOwnerError(null);
    setShowNewOwnerModal(true);
  };

  const closeNewOwnerModal = () => {
    setShowNewOwnerModal(false);
    setNewOwnerForm(EMPTY_NEW_OWNER);
    setOwnerError(null);
  };

  const ownerField = (key, value) =>
    setNewOwnerForm(prev => ({ ...prev, [key]: value }));

  const handleCreateOwner = async () => {
    if (!newOwnerForm.email) {
      setOwnerError('Email is required.');
      return;
    }
    setSavingOwner(true);
    setOwnerError(null);
    try {
      const { data: newOwner } = await axios.post(`${API_BASE}/owners/`, newOwnerForm, { withCredentials: true });
      setClients(prev => [...prev, newOwner]);
      field('owner_id', newOwner.id);
      showToast(`Owner ${newOwner.email} created successfully`, 'success');
      closeNewOwnerModal();
    } catch (err) {
      setOwnerError(formatErrorMessage(err, 'Could not create owner.'));
    } finally {
      setSavingOwner(false);
    }
  };

  /* ── Filter ─────────────────────────────────────────────── */
  const filtered = aircraft.filter(a =>
    a.tail_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.aircraft_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── Lookup badge ───────────────────────────────────────── */
  const LookupBadge = () => {
    if (!lookupStatus) return null;
    const map = {
      loading:  { icon: <Loader size={14} className="animate-spin" />, text: 'Searching FAA…',            color: 'text-blue-400'   },
      found:    { icon: <CheckCircle size={14} />,                      text: 'FAA data imported ✓',      color: 'text-green-400'  },
      notfound: { icon: <AlertCircle size={14} />,                      text: 'Not found — manual entry', color: 'text-yellow-400' },
      error:    { icon: <AlertCircle size={14} />,                      text: 'Lookup error',              color: 'text-red-400'    },
    };
    const { icon, text, color } = map[lookupStatus];
    return (
      <div className={`flex items-center gap-1 text-xs mt-1 ${color}`}>
        {icon} {text}
      </div>
    );
  };

  /* ── Loading / Error ────────────────────────────────────── */
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />Loading…
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 px-4">
      <p className="text-red-400 text-center">{error}</p>
      <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
    </div>
  );

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Aircraft Management</h2>
          <p className="text-gray-400 text-xs md:text-sm mt-1">Aircraft registered at KBOE · {aircraft.length} total</p>
        </div>
        <button onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center">
          <Plus size={18} /> New aircraft
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input type="text" placeholder="Search by tail number, type or owner…"
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm md:text-base" />
      </div>

      {/* Cards */}
      {filtered.length === 0
        ? <p className="text-center text-gray-500 py-16">No aircraft found.</p>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filtered.map(ac => (
              <div key={ac.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-3 md:p-4 hover:border-gray-600 transition">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Plane className="text-blue-400 flex-shrink-0" size={20} />
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-sm md:text-base truncate">{ac.tail_number}</h3>
                      <p className="text-gray-400 text-xs truncate">
                        {ac.aircraft_type || `${ac.manufacturer || ''} ${ac.model || ''}`.trim() || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEditModal(ac)} className="text-blue-400 hover:text-blue-300 p-1"><Edit2 size={16} /></button>
                    <button onClick={() => handleDelete(ac)}  className="text-red-400  hover:text-red-300  p-1"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="space-y-1 text-xs md:text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <User size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{ac.owner_name || ac.owner_company || 'Unknown owner'}</span>
                  </div>
                  {(ac.owner_city || ac.owner_state) && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs pl-5">
                      <span>{[ac.owner_city, ac.owner_state, ac.owner_zip].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-300">
                    <Wrench size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{[ac.manufacturer, ac.model].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <MapPin size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">
                      {ac.fuel_type || '—'}
                      {ac.fuel_capacity_gallons
                        ? ` · ${ac.fuel_capacity_gallons} gal`
                        : ac.fuel_capacity_liters ? ` · ${ac.fuel_capacity_liters} L` : ''}
                      {ac.parking_spot ? ` · ${ac.parking_spot}` : ''}
                    </span>
                  </div>
                  {(ac.max_takeoff_weight_lbs || ac.mtow_kg) && (
                    <div className="text-xs text-gray-500">
                      MTOW: {ac.max_takeoff_weight_lbs
                        ? `${ac.max_takeoff_weight_lbs.toLocaleString()} lbs`
                        : `${ac.mtow_kg?.toLocaleString()} kg`}
                    </div>
                  )}
                  {ac.year_manufactured && <div className="text-xs text-gray-500">Year: {ac.year_manufactured}</div>}
                </div>
              </div>
            ))}
          </div>
        )
      }

      {/* ── Aircraft Modal ─────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95vw] sm:max-w-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">

            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  {editingAircraft ? `Edit ${editingAircraft.tail_number}` : 'New aircraft'}
                </h3>
                {!editingAircraft && (
                  <p className="text-gray-400 text-xs mt-1 hidden sm:block">
                    Enter the tail number — FAA data loads automatically.
                  </p>
                )}
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-white p-1"><X size={20} /></button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-5 flex-1">

              {/* Identification */}
              <Section title="Identification">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label>Tail Number *</Label>
                    <input type="text" placeholder="ex: N12345" value={formData.tail_number}
                      onChange={e => handleTailNumberChange(e.target.value)}
                      disabled={!!editingAircraft}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm disabled:opacity-50" />
                    <LookupBadge />
                  </div>
                  <div>
                    <Label>Manufacturer</Label>
                    <input type="text" placeholder="ex: Cessna" value={formData.manufacturer}
                      onChange={e => field('manufacturer', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <input type="text" placeholder="ex: 172 Skyhawk" value={formData.model}
                      onChange={e => field('model', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Aircraft Type</Label>
                    <input type="text" placeholder="ex: LandPlane" value={formData.aircraft_type}
                      onChange={e => field('aircraft_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Year Manufactured</Label>
                    <input type="number" placeholder="ex: 2018" value={formData.year_manufactured}
                      onChange={e => field('year_manufactured', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Serial Number</Label>
                    <input type="text" value={formData.serial_number}
                      onChange={e => field('serial_number', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>ICAO Code</Label>
                    <input type="text" placeholder="ex: C172" value={formData.icao_code}
                      onChange={e => field('icao_code', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Callsign</Label>
                    <input type="text" value={formData.callsign}
                      onChange={e => field('callsign', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

              {/* Engine & Fuel */}
              <Section title="Engine & Fuel">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Engine Type</Label>
                    <input type="text" placeholder="ex: Turbo-prop" value={formData.engine_type}
                      onChange={e => field('engine_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Engine Count</Label>
                    <input type="number" min="1" max="8" value={formData.engine_count}
                      onChange={e => field('engine_count', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Engine Manufacturer</Label>
                    <input type="text" placeholder="ex: Lycoming" value={formData.engine_manufacturer}
                      onChange={e => field('engine_manufacturer', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Engine Model</Label>
                    <input type="text" value={formData.engine_model}
                      onChange={e => field('engine_model', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Seats</Label>
                    <input type="number" min="1" value={formData.seats}
                      onChange={e => field('seats', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Fuel Type</Label>
                    <select value={formData.fuel_type} onChange={e => field('fuel_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                      <option value="100LL">100LL (Avgas)</option>
                      <option value="JetA">Jet-A</option>
                      <option value="JetA_plus">Jet-A Plus</option>
                    </select>
                  </div>
                  <div>
                    <Label>Fuel Capacity (US gal)</Label>
                    <input type="number" min="0" step="0.1" value={formData.fuel_capacity_gallons}
                      onChange={e => field('fuel_capacity_gallons', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    {formData.fuel_capacity_gallons > 0 && (
                      <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(formData.fuel_capacity_gallons))} L</p>
                    )}
                  </div>
                  <div>
                    <Label>Max Fuel (US gal)</Label>
                    <input type="number" min="0" step="0.1" value={formData.max_fuel_gallons}
                      onChange={e => field('max_fuel_gallons', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    {formData.max_fuel_gallons > 0 && (
                      <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(formData.max_fuel_gallons))} L</p>
                    )}
                  </div>
                </div>
              </Section>

              {/* Weights */}
              <Section title="Weights (US lbs)">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Max Takeoff Weight (lbs)</Label>
                    <input type="number" min="0" value={formData.max_takeoff_weight_lbs}
                      onChange={e => field('max_takeoff_weight_lbs', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    {formData.max_takeoff_weight_lbs > 0 && (
                      <p className="text-gray-500 text-xs mt-0.5">
                        ≈ {lbsToKg(parseInt(formData.max_takeoff_weight_lbs)).toLocaleString()} kg
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Max Landing Weight (lbs)</Label>
                    <input type="number" min="0" value={formData.max_landing_weight_lbs}
                      onChange={e => field('max_landing_weight_lbs', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    {formData.max_landing_weight_lbs > 0 && (
                      <p className="text-gray-500 text-xs mt-0.5">
                        ≈ {lbsToKg(parseInt(formData.max_landing_weight_lbs)).toLocaleString()} kg
                      </p>
                    )}
                  </div>
                </div>
              </Section>

              {/* Owner & Position */}
              <Section title="Owner & Position">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                  {/* Owner select + bouton New Owner */}
                  <div className="sm:col-span-2">
                    <Label>Owner (account)</Label>
                    <div className="flex gap-2">
                      <select
                        value={formData.owner_id}
                        onChange={e => field('owner_id', e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                      >
                        <option value="">— No account linked —</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.company_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={openNewOwnerModal}
                        title="Create new owner"
                        className="flex items-center gap-1.5 px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition whitespace-nowrap flex-shrink-0"
                      >
                        <UserPlus size={14} /> New
                      </button>
                    </div>
                    {!formData.owner_id && (
                      <p className="text-yellow-500/80 text-xs mt-1">
                        Optional — you can link an account later.
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Owner Name (registry)</Label>
                    <input type="text" value={formData.owner_name}
                      onChange={e => field('owner_name', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Street Address</Label>
                    <input type="text" value={formData.owner_address}
                      onChange={e => field('owner_address', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <input type="text" value={formData.owner_city}
                      onChange={e => field('owner_city', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>State</Label>
                    <input type="text" placeholder="TX" maxLength={2}
                      value={formData.owner_state}
                      onChange={e => field('owner_state', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>ZIP Code</Label>
                    <input type="text" placeholder="78006" value={formData.owner_zip}
                      onChange={e => field('owner_zip', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <input type="text" value={formData.owner_country}
                      onChange={e => field('owner_country', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <input type="tel" placeholder="830-336-2518" value={formData.owner_phone}
                      onChange={e => field('owner_phone', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <input type="email" value={formData.owner_email}
                      onChange={e => field('owner_email', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Home Base</Label>
                    <input type="text" placeholder="ex: KBOE" value={formData.home_base}
                      onChange={e => field('home_base', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Parking Spot</Label>
                    <input type="text" value={formData.parking_spot}
                      onChange={e => field('parking_spot', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

              {/* Certificates */}
              <Section title="Certificates">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Airworthiness Date</Label>
                    <input type="date" value={formData.airworthiness_date}
                      onChange={e => field('airworthiness_date', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Certificate Issue Date</Label>
                    <input type="date" value={formData.certificate_issue_date}
                      onChange={e => field('certificate_issue_date', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

            </div>

            {/* Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={closeModal}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition w-full sm:w-auto">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !formData.tail_number}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition w-full sm:w-auto"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── New Owner sub-modal (z-60, par-dessus le modal aircraft) ── */}
      {showNewOwnerModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl">

            {/* Header */}
            <div className="p-5 border-b border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-green-400" />
                <h3 className="text-white font-bold">New Owner</h3>
              </div>
              <button onClick={closeNewOwnerModal} className="text-gray-400 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3">
              {ownerError && (
                <div className="bg-red-900/40 border border-red-700 text-red-300 text-xs rounded-lg px-3 py-2">
                  {ownerError}
                </div>
              )}
              <div>
                <Label>Email *</Label>
                <input
                  type="email"
                  placeholder="pilot@example.com"
                  value={newOwnerForm.email}
                  onChange={e => ownerField('email', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name</Label>
                  <input
                    type="text"
                    value={newOwnerForm.first_name}
                    onChange={e => ownerField('first_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <input
                    type="text"
                    value={newOwnerForm.last_name}
                    onChange={e => ownerField('last_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <input
                  type="tel"
                  placeholder="830-336-2518"
                  value={newOwnerForm.phone}
                  onChange={e => ownerField('phone', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-700 flex justify-end gap-2">
              <button
                onClick={closeNewOwnerModal}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOwner}
                disabled={savingOwner || !newOwnerForm.email}
                className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition flex items-center gap-2"
              >
                {savingOwner
                  ? <><Loader size={14} className="animate-spin" /> Creating…</>
                  : <><UserPlus size={14} /> Create & Select</>}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3 border-b border-gray-700 pb-1">
      {title}
    </h4>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="text-gray-400 text-xs mb-1 block">{children}</label>
);

export default AircraftView;